import React, { useEffect, useState } from "react";
import { api, formatDate } from "../../lib/api";
import { renderWithBold } from "../../lib/markdown";
import { Save, RotateCcw, BookOpen } from "lucide-react";
import { toast } from "sonner";

const MAX = 4000;

export default function AboutTab() {
  const [body, setBody] = useState("");
  const [original, setOriginal] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/about");
      setBody(data.body || "");
      setOriginal(data.body || "");
      setUpdatedAt(data.updated_at);
    } catch {
      toast.error("Failed to load About Us");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirty = body !== original;

  const save = async (e) => {
    e.preventDefault();
    if (saving || !dirty) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/about", { body });
      setOriginal(data.body || "");
      setUpdatedAt(data.updated_at);
      toast.success("About Us updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const paragraphs = body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div data-testid="about-tab">
      <header className="mb-8">
        <p className="font-script text-2xl" style={{ color: "var(--text-accent)" }}>
          tell your story
        </p>
        <h1 className="font-display text-4xl" style={{ color: "var(--text-primary)" }}>
          About Us
        </h1>
        <p className="text-xs mt-1 flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
          <BookOpen size={12} />
          Separate paragraphs with a blank line · wrap words in **double asterisks** to bold them · the heading &amp; greeting on the homepage stay fixed
        </p>
      </header>

      {loading ? (
        <p className="font-script text-xl" style={{ color: "var(--text-secondary)" }}>
          loading…
        </p>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <form
            onSubmit={save}
            className="bg-white rounded-2xl p-6"
            style={{ border: "1px solid var(--border-soft)" }}
          >
            <label className="text-sm font-semibold block mb-2" style={{ color: "var(--text-primary)" }}>
              Body text
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, MAX))}
              rows={14}
              className="w-full px-4 py-3 rounded-xl outline-none resize-none font-body text-sm leading-relaxed"
              style={{
                backgroundColor: "var(--bg-secondary)",
                border: "1px solid var(--border-soft)",
                color: "var(--text-primary)",
              }}
              data-testid="about-body-input"
            />
            <div
              className="flex items-center justify-between text-xs mt-2"
              style={{ color: "var(--text-secondary)" }}
            >
              <span data-testid="about-char-counter">
                {body.length} / {MAX} characters
              </span>
              {updatedAt && <span>Last updated: {formatDate(updatedAt)}</span>}
            </div>

            <div className="flex gap-2 pt-4">
              <button
                type="button"
                onClick={() => setBody(original)}
                disabled={!dirty}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-40"
                style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)" }}
                data-testid="about-revert-button"
              >
                <RotateCcw size={14} />
                Revert
              </button>
              <button
                type="submit"
                disabled={saving || !dirty}
                className="btn-sage inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"
                data-testid="about-save-button"
              >
                <Save size={16} />
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>

          <div
            className="rounded-2xl p-6 md:p-8"
            style={{ backgroundColor: "var(--bg-secondary)", border: "1px dashed var(--border-soft)" }}
          >
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: "var(--text-secondary)" }}>
              Live preview
            </p>
            <p
              className="font-script text-2xl mb-2"
              style={{ color: "var(--text-accent)" }}
            >
              Hello, friend
            </p>
            <h3
              className="font-display text-3xl mb-4"
              style={{ color: "var(--text-primary)" }}
            >
              About Us
            </h3>
            <div className="space-y-3">
              {paragraphs.length === 0 ? (
                <p
                  className="text-sm italic"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Your story will appear here…
                </p>
              ) : (
                paragraphs.map((p, idx) => (
                  <p
                    key={idx}
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {renderWithBold(p, { color: "var(--text-primary)", fontWeight: 600 })}
                  </p>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
